import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail, formatRON } from "../lib/api";
import { useAuth } from "../lib/auth";
import { can } from "../lib/perms";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { ClipboardList, FileDown, Trash2, Search, ReceiptText, FileText, Pencil, Calendar as CalendarIcon, X as XIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function clientNameSlug(lastName, firstName) {
  const raw = `${(lastName || "").trim()}-${(firstName || "").trim()}`.replace(/^-|-$/g, "");
  return raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

// YYYY-MM-DD in local time (the value <input type="date"> exchanges).
function ymd(date) {
  const tz = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - tz).toISOString().slice(0, 10);
}

// Date range presets — return [from, to] as YYYY-MM-DD or empty strings for "all".
function presetRange(key) {
  const now = new Date();
  if (key === "today") {
    const d = ymd(now);
    return [d, d];
  }
  if (key === "week") {
    // Monday as first day (RO convention).
    const d = new Date(now);
    const day = (d.getDay() + 6) % 7; // 0=Mon..6=Sun
    d.setDate(d.getDate() - day);
    return [ymd(d), ymd(now)];
  }
  if (key === "month") {
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    return [ymd(first), ymd(now)];
  }
  if (key === "lastmonth") {
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    return [ymd(first), ymd(last)];
  }
  return ["", ""];
}

const docLabel = (d) =>
  `${d.type === "deviz" ? "Deviz" : "Factura"} ${d.series || "COS"} ${String(d.number ?? "").padStart(3, "0")}`;

function DocLinks({ inv, onJump }) {
  const refs = [
    ...(inv.source ? [{ ...inv.source, prefix: "din" }] : []),
    ...(inv.derived || []).map((d) => ({ ...d, prefix: "→" })),
  ];
  if (!refs.length) return null;
  return (
    <div className="mt-1 flex flex-col items-start gap-0.5">
      {refs.map((r) => (
        <button
          key={`${r.prefix}-${r.id}`}
          type="button"
          onClick={() => onJump(r.id)}
          className="text-[10px] font-mono text-blue-700 hover:underline whitespace-nowrap"
          data-testid={`doc-link-${inv.id}-${r.id}`}
          title="Mergi la documentul legat"
        >
          {r.prefix} {docLabel(r)}
        </button>
      ))}
    </div>
  );
}

export default function InvoicesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.permissions?.invoices === "all";
  const canCalc = can(user, "calculator");
  const canDeviz = can(user, "deviz");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [confirm, setConfirm] = useState(null); // invoice obj being deleted
  const [downloadingId, setDownloadingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [highlightId, setHighlightId] = useState(null);

  // Jump to a linked document: clear filters so it's visible, scroll + flash it.
  const jumpTo = (id) => {
    setQuery("");
    setDateFrom("");
    setDateTo("");
    setHighlightId(id);
    setTimeout(() => {
      document.querySelector(`[data-testid="invoice-row-${id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
    setTimeout(() => setHighlightId(null), 2500);
  };

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/invoices");
      setRows(data);
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la incarcare");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Polling 3s for live multi-device sync
  useEffect(() => {
    if (confirm) return;
    const tick = () => {
      if (document.visibilityState === "visible") load({ silent: true });
    };
    const id = setInterval(tick, 3000);
    return () => clearInterval(id);
  }, [confirm]);

  const filtered = rows.filter((r) => {
    // Date range filter (compares YYYY-MM-DD lexicographically; inclusive).
    if (dateFrom || dateTo) {
      const created = (r.created_at || "").slice(0, 10);
      if (!created) return false;
      if (dateFrom && created < dateFrom) return false;
      if (dateTo && created > dateTo) return false;
    }
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    const name = `${r.client_last_name || ""} ${r.client_first_name || ""}`.toLowerCase();
    return (
      name.includes(q) ||
      (r.client_cui || "").toLowerCase().includes(q) ||
      (r.client_cnp || "").toLowerCase().includes(q)
    );
  });

  const applyPreset = (key) => {
    const [f, t] = presetRange(key);
    setDateFrom(f);
    setDateTo(t);
  };
  const clearDateRange = () => {
    setDateFrom("");
    setDateTo("");
  };
  const isPresetActive = (key) => {
    const [f, t] = presetRange(key);
    return f === dateFrom && t === dateTo && (f || t);
  };

  const downloadPdf = async (inv) => {
    setDownloadingId(inv.id);
    try {
      const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const slug = clientNameSlug(inv.client_last_name, inv.client_first_name)
        || new Date(inv.created_at).toISOString().replace(/[-:T]/g, "").slice(0, 14);
      const prefix = inv.type === "deviz" ? "deviz" : "factura";
      a.download = `${prefix}-${slug}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la descarcare");
    } finally {
      setDownloadingId(null);
    }
  };

  const doDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${confirm.id}`);
      toast.success("Factura stearsa");
      setConfirm(null);
      await load({ silent: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eroare la stergere");
    } finally {
      setDeleting(false);
    }
  };

  const totalSum = filtered
    .filter((r) => r.type !== "deviz")
    .reduce((acc, r) => acc + Number(r.total || 0), 0);

  const visibleInvoiceCount = filtered.filter((r) => r.type !== "deviz").length;
  const hasDateFilter = !!(dateFrom || dateTo);

  return (
    <div className="p-6 md:p-10 max-w-6xl" data-testid="invoices-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
            Istoric emise
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-black">
            Facturi
          </h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">
            Lista tuturor facturilor emise. Re-descarca PDF-ul oricand sau sterge facturi pentru
            a elibera spatiu.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            {hasDateFilter ? "Total in interval" : "Total facturat"}
          </span>
          <span
            className="font-display text-2xl font-black tracking-tighter text-black"
            data-testid="invoices-total-sum"
          >
            {formatRON(totalSum)}
          </span>
          <span className="font-mono text-[10px] text-zinc-500">
            {visibleInvoiceCount} {visibleInvoiceCount === 1 ? "factura" : "facturi"}
            {hasDateFilter && rows.length !== filtered.length
              ? ` din ${rows.length} total`
              : ""}
          </span>
        </div>
      </div>

      {/* Search + date range filter */}
      <div className="mb-4 space-y-2">
        <div className="flex flex-col md:flex-row md:items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cauta dupa nume, CUI sau CNP..."
              className="rounded-sm border-zinc-300 focus-visible:ring-black pl-9"
              data-testid="invoices-search-input"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <CalendarIcon size={14} className="text-zinc-500 mr-0.5" />
            {[
              { key: "today", label: "Azi" },
              { key: "week", label: "Saptamana" },
              { key: "month", label: "Luna" },
              { key: "lastmonth", label: "Luna trecuta" },
            ].map((p) => {
              const active = isPresetActive(p.key);
              return (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className={`px-2.5 py-1 rounded-sm border font-mono uppercase text-[10px] tracking-wider transition ${
                    active
                      ? "bg-black text-white border-black"
                      : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"
                  }`}
                  data-testid={`date-preset-${p.key}`}
                >
                  {p.label}
                </button>
              );
            })}
            {hasDateFilter && (
              <button
                type="button"
                onClick={clearDateRange}
                className="px-2 py-1 rounded-sm border border-zinc-300 bg-white text-zinc-600 hover:text-black hover:border-zinc-500 flex items-center gap-1 font-mono uppercase text-[10px] tracking-wider"
                data-testid="date-clear"
                title="Sterge filtru pe interval"
              >
                <XIcon size={11} /> Sterge
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono uppercase text-[10px] tracking-widest text-zinc-500">
            Interval custom:
          </span>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-40 text-sm"
            data-testid="date-from"
            title="De la data"
          />
          <span className="text-zinc-400 font-mono text-xs">→</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="rounded-sm border-zinc-300 focus-visible:ring-black h-9 w-40 text-sm"
            data-testid="date-to"
            title="Pana la data"
          />
        </div>
      </div>

      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <Table data-testid="invoices-table">
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Data
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Tip
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                Client
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                CUI / CNP
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right">
                Articole
              </TableHead>
              <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide text-right">
                Total
              </TableHead>
              {isAdmin && (
                <TableHead className="text-black font-semibold uppercase text-[10px] tracking-wide">
                  Creat de
                </TableHead>
              )}
              <TableHead className="w-32" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-zinc-500 py-10">
                  Se incarca...
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isAdmin ? 8 : 7} className="text-center text-zinc-500 py-10">
                  <ReceiptText size={28} className="mx-auto mb-2 text-zinc-300" />
                  {rows.length === 0
                    ? "Niciun document emis inca. Genereaza unul din pagina Calculator sau Deviz."
                    : "Niciun rezultat pentru cautarea ta."}
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((inv) => {
                const date = new Date(inv.created_at);
                const dateStr = isNaN(date.getTime())
                  ? "-"
                  : date.toLocaleString("ro-RO", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    });
                const fullName = `${inv.client_last_name || ""} ${inv.client_first_name || ""}`.trim() || "—";
                const idDoc = inv.client_cui || inv.client_cnp || "—";
                const isDeviz = inv.type === "deviz";
                return (
                  <TableRow
                    key={inv.id}
                    data-testid={`invoice-row-${inv.id}`}
                    className={`transition-colors duration-500 ${highlightId === inv.id ? "bg-amber-100" : ""}`}
                  >
                    <TableCell className="font-mono text-xs text-zinc-700">{dateStr}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider ${
                          isDeviz
                            ? "bg-amber-50 text-amber-800 border border-amber-200"
                            : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                        }`}
                        data-testid={`invoice-type-${inv.id}`}
                      >
                        {isDeviz ? "Deviz" : "Factura"}
                      </span>
                      <DocLinks inv={inv} onJump={jumpTo} />
                    </TableCell>
                    <TableCell className="text-black font-medium">{fullName}</TableCell>
                    <TableCell className="font-mono text-xs text-zinc-700">{idDoc}</TableCell>
                    <TableCell className="text-right font-mono text-zinc-700">
                      {inv.items_count}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-black">
                      {isDeviz ? "—" : formatRON(inv.total)}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="font-mono text-xs text-zinc-700">
                        {inv.created_by_label || "—"}
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {isDeviz ? (
                          <>
                            {canCalc && (
                              <Link to={`/calculator?from=${inv.id}`}>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 hover:bg-emerald-50"
                                data-testid={`finalize-deviz-${inv.id}`}
                                title="Finalizeaza ca factura (preia datele din deviz)"
                              >
                                <FileText size={14} className="text-emerald-700" />
                              </Button>
                            </Link>
                            )}
                            {canDeviz && (
                              <Link to={`/deviz?from=${inv.id}`}>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 hover:bg-blue-50"
                                data-testid={`edit-deviz-${inv.id}`}
                                title="Editeaza devizul"
                              >
                                <Pencil size={14} className="text-blue-700" />
                              </Button>
                            </Link>
                            )}
                          </>
                        ) : (
                          <>
                            {canDeviz && (
                              <Link to={`/deviz?from=${inv.id}`}>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 hover:bg-amber-50"
                                  data-testid={`invoice-to-deviz-${inv.id}`}
                                  title="Fa deviz din factura"
                                >
                                  <ClipboardList size={14} className="text-amber-700" />
                                </Button>
                              </Link>
                            )}
                            {canCalc && (
                              <Link to={`/calculator?from=${inv.id}`}>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 hover:bg-blue-50"
                                  data-testid={`edit-invoice-${inv.id}`}
                                  title="Editeaza factura (genereaza o noua factura pre-completata)"
                                >
                                  <Pencil size={14} className="text-blue-700" />
                                </Button>
                              </Link>
                            )}
                          </>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => downloadPdf(inv)}
                          disabled={downloadingId === inv.id}
                          className="h-8 w-8 hover:bg-zinc-100"
                          data-testid={`download-invoice-${inv.id}`}
                          title="Descarca PDF"
                        >
                          <FileDown size={14} className="text-black" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setConfirm(inv)}
                          className="h-8 w-8 hover:bg-red-50"
                          data-testid={`delete-invoice-${inv.id}`}
                          title="Sterge"
                        >
                          <Trash2 size={14} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Sterge factura?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm && (
                <>
                  Vei sterge factura emisa pe numele{" "}
                  <span className="font-semibold text-black">
                    {`${confirm.client_last_name || ""} ${confirm.client_first_name || ""}`.trim() || "client necunoscut"}
                  </span>{" "}
                  in valoare de{" "}
                  <span className="font-semibold text-black">{formatRON(confirm.total)}</span>.
                  <br />
                  <span className="text-red-600">Aceasta actiune este definitiva.</span>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Anuleaza</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-confirm"
            >
              {deleting ? "Se sterge..." : "Sterge definitiv"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
